/**
 * @bos/portal3 — THE ORDERS TRAY (phase 2): the basket, docked.
 *
 * This is a MOVE, not a rewrite. Every law the basket carried when it lived inside
 * `DataModelReview` is here verbatim, because the laws are what the basket is:
 *
 *   - ORDER IS SIGNIFICANT: the list is ordered and reorderable, and it says so — each edit
 *     lands on the model as the ones before it left it.
 *   - EVERY MUTATION DROPS THE PREVIEW: a combined preview must only ever describe the
 *     basket on screen. (The parent owns that rule — it bumps `basketVersion` — and this
 *     component simply never renders a preview it was not given.)
 *   - THE PREVIEW IS CHAINED, SERVER-SIDE: what you read is what the apply will do.
 *   - ALL-OR-NOTHING: nothing is applied while any member refuses, and each refusal is
 *     ATTRIBUTED to the exact edit that caused it, so a person fixes or drops that one
 *     without discarding the set.
 *   - THE COST IS SAID BEFORE THE CLICK, in work and never in numbers.
 *
 * The component is presentational: `DataModelReview` keeps the basket array, the version
 * counter, the busy flag and every callback. Nothing here talks to the server, and nothing
 * here holds state that could disagree with the parent's.
 */
import type { DataModelEdit, RunDataModelBasketPreview } from '@bos/portal3-server';

/** One basket row in the person's terms — a client rendering of the edit they composed (the
 *  server's own sentence for it arrives with the combined preview and the ledger record). */
export function editLine(edit: DataModelEdit): string {
  switch (edit.op) {
    case 'rename-field':
      return `Rename ${edit.entity}.${edit.from} to '${edit.to}'`;
    case 'retype-field':
      return `Change ${edit.entity}.${edit.field} to ${edit.type}`;
    case 'add-field':
      return `Add '${edit.field}' (${edit.type}${edit.format !== undefined ? `, ${edit.format}` : ''}) to ${edit.entity}`;
    case 'remove-field':
      return `Remove ${edit.entity}.${edit.field}`;
    case 'rename-entity':
      return `Rename ${edit.from} to ${edit.to}`;
    case 'remove-entity':
      return `Remove ${edit.entity} from the model`;
  }
}

/** The combined preview, rendered item by item IN ORDER — every refusal attributed to the exact edit
 *  that caused it, every no-op named as such, and the all-or-nothing law stated before the apply. */
export function BasketPreviewBlock({
  preview,
  busy,
  onApply,
  onDismiss,
}: {
  readonly preview: RunDataModelBasketPreview;
  readonly busy: boolean;
  readonly onApply: () => void;
  readonly onDismiss: () => void;
}) {
  const refusedCount = preview.items.filter((i) => i.outcome.kind === 'refused').length;
  const changeCount = preview.items.filter((i) => i.outcome.kind === 'change').length;
  return (
    <div className="dm-edit__preview" role="region" aria-label="Before it runs">
      <p className="dm-edit__sentence">Together, in this order, your edits come to this:</p>
      <ol className="dm-basket__outcomes">
        {preview.items.map((item, i) => (
          <li key={i} className={`dm-basket__outcome dm-basket__outcome--${item.outcome.kind}`}>
            <span className="dm-basket__outcome-head">{item.summary}</span>
            {item.outcome.kind === 'change' && (
              <>
                <ul className="dm-edit__list">
                  {item.outcome.changes.map((c, j) => (
                    <li key={j}>{c}</li>
                  ))}
                </ul>
                {item.outcome.cautions.length > 0 && (
                  <ul className="dm-edit__list dm-edit__list--cautions">
                    {item.outcome.cautions.map((c, j) => (
                      <li key={j}>{c}</li>
                    ))}
                  </ul>
                )}
              </>
            )}
            {item.outcome.kind === 'no-op' && (
              <p className="dm-edit__sentence dm-basket__noop">
                {item.outcome.reason} — this edit orders nothing; the edits before it (or the model as it
                stands) already made it true.
              </p>
            )}
            {item.outcome.kind === 'refused' && (
              <>
                <p className="dm-edit__sentence">This edit cannot be applied as things stand:</p>
                <ul className="dm-edit__list">
                  {item.outcome.refusals.map((r, j) => (
                    <li key={j}>{r}</li>
                  ))}
                </ul>
                <p className="dm-edit__sentence">
                  Fix what it names, or remove this edit — the rest of the basket stays.
                </p>
              </>
            )}
          </li>
        ))}
      </ol>
      {refusedCount > 0 && (
        <p className="dm-edit__consequence">
          A basket applies all-or-nothing: nothing will be changed while any edit above refuses. The
          outcomes shown after a refused edit are what the others come to WITHOUT it.
        </p>
      )}
      {refusedCount === 0 && changeCount > 0 && (
        <p className="dm-edit__consequence">
          These edits are exact — applied as ordered, not asked for, all at once. Making them needs no new
          derivation and spends nothing.{' '}
          {preview.laterStagesDerived
            ? 'Parts of the plan already built on the data model will be rebuilt from the edited model when the run continues — that is paid work.'
            : 'Nothing has been built on the data model yet, so nothing is redone — everything later simply builds on the edited model.'}{' '}
          The run pauses here again so you can see the combined change before approving.
        </p>
      )}
      {refusedCount === 0 && changeCount === 0 && (
        <p className="dm-edit__consequence">Everything in the basket is already true — there is nothing to apply.</p>
      )}
      <div className="dm-edit__actions">
        <button
          className="btn btn--primary btn--sm"
          type="button"
          onClick={onApply}
          disabled={busy || refusedCount > 0 || changeCount === 0}
        >
          {busy ? 'Applying…' : changeCount === 1 ? 'Apply this edit' : `Apply these ${changeCount} edits as one`}
        </button>
        <button className="btn btn--quiet btn--sm" type="button" onClick={onDismiss} disabled={busy}>
          Keep reading
        </button>
      </div>
    </div>
  );
}

export interface OrdersTrayProps {
  /** The composed set, in the order it applies. */
  readonly basket: readonly DataModelEdit[];
  /** The server's combined dry run, or null while none is on screen. */
  readonly preview: RunDataModelBasketPreview | null;
  readonly busy: boolean;
  /** The apply's own outcome sentence — it OUTLIVES the emptied basket, so the tray stays
   *  mounted for it (the same law the approve note follows). */
  readonly note: string | null;
  readonly onMove: (index: number, delta: -1 | 1) => void;
  readonly onRemove: (index: number) => void;
  readonly onEmpty: () => void;
  readonly onPreview: () => void;
  readonly onApply: () => void;
  readonly onDismissPreview: () => void;
}

export function OrdersTray({
  basket,
  preview,
  busy,
  note,
  onMove,
  onRemove,
  onEmpty,
  onPreview,
  onApply,
  onDismissPreview,
}: OrdersTrayProps) {
  if (basket.length === 0 && note === null) return null;
  return (
    <section className="dm-basket dm-tray" role="region" aria-label="Your basket">
      {basket.length > 0 && (
        <>
          <h3 className="dm__section">
            Your basket — {basket.length} edit{basket.length === 1 ? '' : 's'}, applied as one
          </h3>
          <p className="dm-basket__order">
            They apply in this order: each edit lands on the model as the ones before it left it. Reorder if
            one depends on another.
          </p>
          <ol className="dm-basket__list">
            {basket.map((e, i) => (
              <li key={`${i}:${editLine(e)}`} className="dm-basket__row">
                <span className="dm-basket__what">{editLine(e)}</span>
                <span className="dm-basket__tools">
                  <button
                    className="btn btn--quiet btn--sm"
                    type="button"
                    onClick={() => onMove(i, -1)}
                    disabled={busy || i === 0}
                    aria-label={`Move up: ${editLine(e)}`}
                  >
                    Up
                  </button>
                  <button
                    className="btn btn--quiet btn--sm"
                    type="button"
                    onClick={() => onMove(i, 1)}
                    disabled={busy || i === basket.length - 1}
                    aria-label={`Move down: ${editLine(e)}`}
                  >
                    Down
                  </button>
                  <button
                    className="btn btn--quiet btn--sm"
                    type="button"
                    onClick={() => onRemove(i)}
                    disabled={busy}
                    aria-label={`Remove: ${editLine(e)}`}
                  >
                    Remove
                  </button>
                </span>
              </li>
            ))}
          </ol>
          {preview === null && (
            <div className="dm-edit__actions">
              <button className="btn btn--ghost btn--sm" type="button" onClick={onPreview} disabled={busy}>
                {busy ? 'Checking…' : 'See what these will do — together'}
              </button>
              <button className="btn btn--quiet btn--sm" type="button" onClick={onEmpty} disabled={busy}>
                Empty the basket
              </button>
            </div>
          )}
          {preview !== null && (
            <BasketPreviewBlock preview={preview} busy={busy} onApply={onApply} onDismiss={onDismissPreview} />
          )}
        </>
      )}
      {note !== null && (
        <p className="dm-door__note" role="status">
          {note}
        </p>
      )}
    </section>
  );
}
